import { lazy, Suspense, useEffect } from 'react';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import ErrorState from '@zenbusiness/zui/ErrorState';
import Stack from '@zenbusiness/zui/Stack';

import CarouselStep from '#client/components/CarouselStep';
import ErrorBoundary from '#client/components/ErrorBoundary';
import useWhatsNewBannerContext from '#client/hooks/useWhatNewBannerContext';

import WhatsNewBanner from './components/Banner';

import useMapBanner from './hooks/useMapBanners';

const SwipeableViews = lazy(() => import('react-swipeable-views'));

function WhatsNewBanners() {
  const { whatNewBanners, activeStep, setActiveStep } = useWhatsNewBannerContext();

  const banners = useMapBanner(whatNewBanners);
  const maxSteps = banners.length;
  const hideStep = maxSteps === 1;

  const bannerDisplayedTracker = useClickstreamCallback('bannerDisplayed');

  const handleNext = () => {
    const nextStepIndex = activeStep === maxSteps - 1 ? 0 : activeStep + 1;
    setActiveStep(nextStepIndex);

    const {
      eventProps: { elementName, bannerType, appPageName }
    } = banners[nextStepIndex];

    bannerDisplayedTracker({
      elementName,
      bannerType: bannerType || 'info',
      appPageName: appPageName || 'active_banking_dashboard'
    });
  };

  const handleBack = () => {
    setActiveStep(activeStep === 0 ? maxSteps - 1 : activeStep - 1);
  };

  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  useEffect(() => {
    if (banners?.length > 0) {
      const {
        eventProps: { elementName, bannerType, appPageName }
      } = banners[0];
      bannerDisplayedTracker({
        elementName,
        bannerType: bannerType || 'info',
        appPageName: appPageName || 'active_banking_dashboard'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banners?.[0]?.type]);

  if (!banners.length) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <ErrorBoundary
        fallback={
          <ErrorState
            slotProps={{
              primaryAction: {
                eventProps: {
                  pageSection: 'banking'
                }
              }
            }}
          />
        }
      >
        <Stack
          gap="8px"
          mb={{ xs: '16px', md: '32px' }}
        >
          <Stack>
            <SwipeableViews
              enableMouseEvents
              index={activeStep}
              onChangeIndex={handleStepChange}
            >
              {banners.map((step, index) =>
                Math.abs(activeStep - index) <= 2 ? (
                  <WhatsNewBanner
                    buttonProps={step.buttonProps}
                    description={step.description}
                    handleClose={step.handleClose}
                    imageUrl={step.imageUrl}
                    key={step.type}
                    pill={step.pill}
                    title={step.title}
                    variant={step.variant}
                  />
                ) : null
              )}
            </SwipeableViews>
          </Stack>
          {!hideStep && (
            <CarouselStep
              activeStep={activeStep}
              handleBack={handleBack}
              handleNext={handleNext}
              maxSteps={maxSteps}
            />
          )}
        </Stack>
      </ErrorBoundary>
    </Suspense>
  );
}

export default WhatsNewBanners;
